import { nextTick } from "vue";

/**
 * Handles form validation errors by scrolling to and focusing on the first error
 * @param {Array} formErrors - Array of form errors from previewRef.value.formErrors
 * @returns {boolean} true if validation passed (no errors), false if there are errors
 */
export const handleFormValidationErrors = async (formErrors) => {
  if (formErrors.length > 0) {
    const firstError = formErrors[0];
    if (firstError?.item) {
      await nextTick(() => {
        const element = firstError.item.vm?.vnode?.el;
        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
          });
          setTimeout(() => {
            const inputElement = element.querySelector("input") || element;
            if (inputElement && typeof inputElement.focus === "function") {
              inputElement.focus();
            }
          }, 300);
        }
      });
    }
    return false;
  }
  return true;
};

/**
 * Calculate the checksum for the NPI number without the check digit.
 * The checksum is calculated using the Luhn algorithm.
 */
const calculateChecksum = (npiWithoutCheckDigit) => {
  const digits = String(npiWithoutCheckDigit).split("").reverse().map(Number);
  let total = 0;

  for (let i = 0; i < digits.length; i++) {
    if (i % 2 === 0) {
      let subtotal = 2 * digits[i];
      if (subtotal > 9) {
        subtotal -= 9;
      }
      total += subtotal;
    } else {
      total += digits[i];
    }
  }

  return (total * 9) % 10;
};

/**
 * Verify if the NPI number is valid using the Luhn algorithm.
 * @param {string} npi - NPI number to verify
 * @returns {boolean} true if the NPI number is valid, false otherwise
 */
export const isNPIValid = (npi, logical = false) => {
  if (!logical) {
    if (!npi) return true;
    if (typeof npi !== "string") {
      return false;
    }
  }
  if (!npi) return false;
  const npiStr = String(npi);

  const cleanedNPIString = npiStr.replace(/\D/g, "");

  // Verify length, first digit and type of characters
  if (cleanedNPIString.length !== 10 || !["1", "2"].includes(cleanedNPIString[0]) || !/^\d+$/.test(cleanedNPIString)) {
    return false;
  }

  // Adding prefix 80840 to the NPI number
  const prefixedNPI = parseInt("80840" + cleanedNPIString);
  const withoutCheckDigit = Math.floor(prefixedNPI / 10);

  // Checksum must be equal to the check digit of NPI number
  return calculateChecksum(withoutCheckDigit) === prefixedNPI % 10;
};

export const validateEditablePdfFields = (formData) => {
  const errors = [];
  const requiredFields = [];

  // Check for signature and date fields
  Object.keys(formData).forEach((fieldName) => {
    const fieldValue = formData[fieldName];
    const isSignatureField =
      fieldName.toLowerCase().includes("signature") || fieldName.toLowerCase().includes("prescriber_signature");
    const isDateField = fieldName.toLowerCase().includes("signature_date");

    if ((isSignatureField || isDateField) && (!fieldValue || fieldValue.trim() === "")) {
      errors.push(fieldName);
      requiredFields.push(fieldName);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
    requiredFields,
  };
};

export const isPhoneNumberValid = (phone) => {
  if (!phone) return true;
  if (typeof phone !== "string") {
    return false;
  }

  // Allow common phone formatting characters: spaces, hyphens, parentheses, dots
  const phoneRegex = /^[\d\s\-\(\)\.]{10,}$/;
  if (!phoneRegex.test(phone)) {
    return false;
  }

  const cleanedPhoneString = phone.replace(/\D/g, "");

  // Verify length and that it contains only digits
  if (cleanedPhoneString.length !== 10 || !/^\d{10}$/.test(cleanedPhoneString)) {
    return false;
  }

  return true;
};

export const isZipCodeValid = (zipCode) => {
  if (!zipCode) return true;

  if (typeof zipCode !== "string") {
    return false;
  }

  // Check for valid 5-digit format: exactly 5 digits, no other characters
  if (/^\d{5}$/.test(zipCode)) {
    return true;
  }

  // Check for valid 9-digit format with dash: exactly "12345-6789"
  if (/^\d{5}-\d{4}$/.test(zipCode)) {
    return true;
  }

  // Check for valid 9-digit format without dash: exactly 9 digits
  if (/^\d{9}$/.test(zipCode)) {
    return true;
  }

  return false;
};

export const isEmailValid = (email) => {
  if (!email) return false;

  if (typeof email !== "string") {
    return false;
  }

  // Standard email regex pattern
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};
